import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SkeletonLoader from 'components/SkeletonLoader';
import Typography from 'components/Typography';

import { QuickReplyType } from 'components/MessageExtensions/QuickReplies/types';
import { useChatContext } from 'hooks/chatContext';
import { useConversation } from 'hooks/conversation';
import { useAnalytics } from 'hooks/useAnalytics';

import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';

import { title } from './content';
import { StyledPopularTopicsWrapper, StyledTopicItem } from './styles';

import { ANALYTICS } from 'analytics/constants';
import { AnalyticQuickReplyDetailsType, AnalyticReconnectedDetailsType } from 'analytics/types';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { QUERY_STATUS } from 'constants/query';
import { ROUTES } from 'routes/constants';

const PopularTopics = ({ id }: { id: string }) => {
  const { trackEvent } = useAnalytics();
  const { chatContextState } = useChatContext();
  const { conversationState, dispatchConversationAction } = useConversation();

  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();

  const popularTopicsContent = chatContextState.popularTopics;

  if (popularTopicsContent.fetchStatus === QUERY_STATUS.ERROR) return null;

  if (popularTopicsContent.fetchStatus === QUERY_STATUS.LOADING) {
    return (
      <StyledPopularTopicsWrapper id={id} role='popular-topics-wrapper'>
        <SkeletonLoader />
      </StyledPopularTopicsWrapper>
    );
  }

  const handleClickQuickReply = async (quickReply: QuickReplyType) => {
    setDisabled(true);
    if (conversationState.isInactivityTimeout) {
      const context = { method: 'Popular topics' };
      DataDogLogger.action(CUSTOM_ACTION_NAMES.INACTIVITY_RECONNECT, context);
      trackEvent({
        analyticEvent: ANALYTICS.EVENT.CHAT_RECONNECTED,
        details: { reconnect_method: context.method } as AnalyticReconnectedDetailsType,
      });
    }
    if (conversationState.isConnected) {
      await window[CHATUI_WINDOW_PATH].sendMessage(quickReply.title, quickReply.payload);
    } else {
      dispatchConversationAction({
        type: ACTIONS.CHANGE,
        payload: {
          isConnected: false,
          isInitializationRequested: true,
          questionWhenInitializing: {
            message: quickReply.title,
            enginePayload: quickReply.payload,
          },
        },
      });
    }
    // Aligned with backend analytics event. Ref: https://docs.google.com/document/d/1PX58MamIZS-plb_O_Z3uPHhJNqipNfbhmQemFawQk9c/edit
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.QUICK_REPLY,
      details: {
        intent_id: quickReply.payload.quick_reply_intent.intent_id,
        intent_class_id: quickReply.payload.quick_reply_intent.intent_class_id,
        quick_reply_text: quickReply.title,
        quick_reply_source: quickReply.payload.source,
        quick_reply_uid: quickReply.payload.uid,
      } as AnalyticQuickReplyDetailsType,
    });

    navigate(ROUTES.CHAT);
  };

  const quickReplies = popularTopicsContent?.data?.quick_replies?.quick_replies;

  return (
    <StyledPopularTopicsWrapper id={id} role='popular-topics-wrapper'>
      <Typography as='h4' color='strong' variant='heading' size='sm'>
        {title}
      </Typography>
      {quickReplies?.map((quickReply: QuickReplyType) => (
        <StyledTopicItem
          role='popular-topics-item'
          disabled={disabled}
          key={quickReply.id}
          onClick={() => handleClickQuickReply(quickReply)}
        >
          <Typography as='span' color='' variant='label' size='md'>
            {quickReply.title}
          </Typography>
        </StyledTopicItem>
      ))}
    </StyledPopularTopicsWrapper>
  );
};

export default PopularTopics;
